'use client'

import { type ReactNode } from 'react'
import Pill from '@/components/ui/Pill'
import Link from '@/components/ui/Link'
import Menu from '@/components/ui/Menu'
import PrimaryButton from '@/components/content/app/PrimaryButton'
import AppIcon from '@/components/content/app/AppIcon'
import Breadcrumb from '@/components/content/sidebar/Breadcrumb'
import ExternalIndicator from '@/components/ui/ExternalIndicator'
import RankIndicator, { type RankChange } from '@/components/ui/RankIndicator'
import { getBrandColors, trimUrl, isPopulatedObject } from '@/lib/utils'
import { checkAppBranding, processAffiliateLink } from '@/lib/formatting'
import { getPrimaryButton, getAppRecommendation } from '@/components/content/app/helpers'
import { determineDealsToDisplay, formatDealButton } from '@/components/content/deals/helpers'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { Recommendation } from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import type { MenuItem } from '@/helpers/menus'
import type {
  App,
  Deal,
  Link as LinkType,
  Category,
  AppCategoryRecommendation,
} from '@/payload-types'

type ReviewSidebarProps = {
  app?: Pick<
    App,
    | 'id'
    | 'slug'
    | 'name'
    | 'deals'
    | 'icon'
    | 'glyph'
    | 'websiteUrl'
    | 'affiliateLink'
    | 'brandPrimaryColor'
    | 'brandBackgroundColor'
    | 'partnershipTier'
    | 'categoryRecommendations'
  >
  compareWithApp?: Pick<App, 'slug'>
  canonicalCompareSlug?: string
  reviewName?: string
  category?: Category
  menu: MenuItem[]
  rank?: number
  rankChange?: RankChange
}

export default function ReviewSidebar({
  app,
  compareWithApp,
  canonicalCompareSlug,
  reviewName,
  category,
  menu,
  rank,
  rankChange,
}: ReviewSidebarProps) {
  const affiliateLink = app?.affiliateLink as LinkType
  const appDeals = app?.deals?.docs as Deal[]
  const formattedDeals = determineDealsToDisplay(appDeals)
  const hasDeals = formattedDeals?.length > 0
  const primaryDeal = formattedDeals[0]

  const slug = app?.slug
  const affiliate = slug
    ? processAffiliateLink(affiliateLink, slug, app?.name, app?.partnershipTier)
    : null
  const link = affiliate ? affiliate.url : null
  const showApp = app && link

  const { backgroundColor } = getBrandColors(app)
  const { appIcon, appGlyph } = checkAppBranding(app as App)

  const handleClick = () => {
    if (hasDeals) {
      localStorage.setItem('efficient:claim', 'true')
    }
  }

  const AffiliateLink = ({ className, children }: { className?: string; children: ReactNode }) =>
    affiliate ? (
      <Link
        href={affiliate.url}
        target="_blank"
        passReferrerUrl={affiliate.passReferrerUrl}
        sponsored={affiliate.isSponsored}
        onClick={handleClick}
        className={className}
      >
        {children}
      </Link>
    ) : null

  const dealLabel = primaryDeal ? formatDealButton(primaryDeal) : undefined

  const categoryApps =
    (category?.appRecommendations?.docs as AppCategoryRecommendation[] | undefined)
      ?.map((rec) => {
        const app = rec.app
        return isPopulatedObject<App>(app) ? app.partnershipTier : null
      })
      .filter((tier): tier is NonNullable<typeof tier> => tier !== null) ?? []

  const { recommendation } = app
    ? getAppRecommendation({ app, categoryId: category?.id ?? 0 })
    : { recommendation: undefined }

  // lg (not md): tablets (768-1023px) are fallback users for desktop-required
  // deals, so they must not be classified as desktop.
  const { lg } = useBreakpoint()

  const primaryButton = app
    ? getPrimaryButton({
        app,
        deal: { count: formattedDeals?.length, label: dealLabel },
        compareWithApp,
        canonicalCompareSlug,
        categoryApps,
        isSummary: true,
        isBest: recommendation === Recommendation.TopPick,
        isBroadCategory: Boolean(category?.isBroadCategory),
        isDesktop: lg,
        desktopIsRequired: primaryDeal?.desktopIsRequired ?? false,
      })
    : null

  return (
    <div className="flex w-full flex-row items-center gap-3.5 md:flex-col md:items-start md:p-4">
      <div className="flex w-full min-w-0 flex-1 items-center gap-3.5">
        {showApp && (
          <div className="flex items-center justify-between md:w-full">
            <div className="flex items-center gap-3.5">
              <AffiliateLink className="h-8 w-8 shrink-0 md:h-14 md:w-14">
                <AppIcon
                  glyph={appGlyph}
                  icon={appIcon}
                  name={app.name}
                  background={backgroundColor}
                  transition={false}
                />
              </AffiliateLink>
              <div className="hidden flex-col gap-0.5 leading-tight md:mt-1 md:flex md:w-[calc(100%-70px)]">
                <AffiliateLink className="truncate font-semibold text-gray-950 dark:text-white">
                  {app.name}
                </AffiliateLink>

                <div className="group inline-flex items-center">
                  <AffiliateLink className="underlined max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-13 text-gray-600 dark:text-zinc-500">
                    {trimUrl(app.websiteUrl)}
                  </AffiliateLink>
                  <ExternalIndicator />
                </div>
              </div>
            </div>
            {typeof rank === 'number' && (
              <div className="hidden md:flex">
                <RankIndicator
                  rank={rank}
                  rankChange={rankChange}
                  orientation="horizontal"
                  href={category?.slug ? `/best/${category.slug}` : undefined}
                />
              </div>
            )}
          </div>
        )}
        <Breadcrumb placeholder="Review" title={reviewName || app?.name} hideSlash={!showApp} />
      </div>
      {showApp && primaryButton && (
        <div className="flex shrink-0 gap-2.5 md:w-full">
          <PrimaryButton
            primaryButton={primaryButton}
            primaryDeal={primaryDeal}
            app={app}
            onClick={handleClick}
            className="flex w-full"
          >
            <Pill style="primary" className="group md:w-full md:justify-center">
              {primaryButton.label}
              {primaryButton.opensInNewTab && <ExternalIndicator />}
            </Pill>
          </PrimaryButton>
          <Menu items={menu} align="right" subAlign="down" />
        </div>
      )}
    </div>
  )
}
